import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/store';
import { setSidebarOpen } from '@/store/slices/uiSlice';
import { logoutUser } from '@/store/slices/authSlice';
import { Link, useNavigate } from 'react-router-dom';
import { X, Home, Building, User, Settings, Info, MessageCircle, LogOut } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { signOut } from '@/services/authService';

const Sidebar: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { sidebarOpen } = useSelector((state: RootState) => state.ui);
  
  // Use both auth sources for maximum reliability
  const supabaseUser = useAuth();
  const { user: reduxUser, isAuthenticated: reduxAuth } = useSelector((state: RootState) => state.auth);
  
  // User is authenticated if either source has user data
  const isAuthenticated = !!supabaseUser || reduxAuth;
  const user = supabaseUser || reduxUser;

  const closeSidebar = () => {
    dispatch(setSidebarOpen(false));
  };

  const handleLogout = async () => {
    try {
      // Sign out from Supabase
      await signOut();
      
      // Clear Redux state
      dispatch(logoutUser() as any);
      
      // Close sidebar and redirect
      closeSidebar();
      navigate('/');
      
      // Force page refresh to ensure all state is cleared
      setTimeout(() => {
        window.location.reload();
      }, 100);
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear Redux state and redirect on error
      dispatch(logoutUser() as any);
      closeSidebar();
      navigate('/');
    }
  };

  if (!sidebarOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
        onClick={closeSidebar}
      />
      
      {/* Sidebar */}
      <div className={`fixed left-0 top-0 h-full w-64 bg-white dark:bg-gray-800 shadow-lg z-50 transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Menu</h2>
          <button
            onClick={closeSidebar}
            className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 lg:hidden"
            aria-label="Close sidebar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <nav className="p-4 space-y-2">
          <Link
            to="/"
            onClick={closeSidebar}
            className="flex items-center space-x-3 p-3 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <Home className="h-5 w-5" />
            <span>Home</span>
          </Link>
          
          <Link
            to="/properties"
            onClick={closeSidebar}
            className="flex items-center space-x-3 p-3 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <Building className="h-5 w-5" />
            <span>Properties</span>
          </Link>
          
          <Link
            to="/about"
            onClick={closeSidebar}
            className="flex items-center space-x-3 p-3 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <Info className="h-5 w-5" />
            <span>About</span>
          </Link>
          
          <Link
            to="/contact"
            onClick={closeSidebar}
            className="flex items-center space-x-3 p-3 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <MessageCircle className="h-5 w-5" />
            <span>Contact</span>
          </Link>
          
          {isAuthenticated ? (
            <>
              <Link
                to={
                  user?.role === 'admin' ? '/admin-dashboard' :
                  user?.role === 'landlord' ? '/landlord-dashboard' :
                  user?.role === 'tenant' ? '/tenant-dashboard' :
                  '/dashboard'
                }
                onClick={closeSidebar}
                className="flex items-center space-x-3 p-3 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <User className="h-5 w-5" />
                <span>Dashboard</span>
              </Link>
              
              <Link
                to="/settings"
                onClick={closeSidebar}
                className="flex items-center space-x-3 p-3 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <Settings className="h-5 w-5" />
                <span>Settings</span>
              </Link>
              
              <hr className="my-2 border-gray-200 dark:border-gray-600" />
              
              <button
                onClick={handleLogout}
                className="flex items-center space-x-3 p-3 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors w-full text-left"
              >
                <LogOut className="h-5 w-5" />
                <span>Logout</span>
              </button>
            </>
          ) : (
            <>
              <hr className="my-2 border-gray-200 dark:border-gray-600" />
              
              <Link
                to="/login"
                onClick={closeSidebar}
                className="flex items-center space-x-3 p-3 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <User className="h-5 w-5" />
                <span>Login</span>
              </Link>
              
              <Link
                to="/register"
                onClick={closeSidebar}
                className="flex items-center space-x-3 p-3 rounded-md bg-primary-600 text-white hover:bg-primary-700 transition-colors"
              >
                <User className="h-5 w-5" />
                <span>Register</span>
              </Link>
              
              <Link
                to="/list-property"
                onClick={closeSidebar}
                className="flex items-center space-x-3 p-3 rounded-md border border-primary-600 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
              >
                <Building className="h-5 w-5" />
                <span>List Property</span>
              </Link>
            </>
          )}
        </nav>
      </div>
    </>
  );
};

export default Sidebar; 